/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import {
  addEventToFavorites,
  loadPlaylistsIndex,
  removeEventFromFavorites,
  removePlaylist,
  renamePlaylist,
  saveCurrentPlaylist,
  FAVORITES_TITLE,
} from './favoritesService';

const initialState = {
  playlists: [],   // Array<{ id, title, videos: [{ id, details: { event_id, ... }, ... }] }>
  loading: false,
  error: null,
};

// Pull a video row's event_id wherever the API has it (top-level or nested
// under `details`). Old payloads vary across staging/prod, so we look in both.
const eventIdOf = (v) => v?.event_id ?? v?.eventId ?? v?.details?.event_id ?? null;

const slice = createSlice({
  name: 'playerFavorites',
  initialState,
  reducers: {
    clearFavorites: () => initialState,
    playlistCreatedLocally: (state, action) => {
      const created = action.payload;
      if (!created || created.id == null) return;
      if (state.playlists.some((p) => p.id === created.id)) return;
      state.playlists.unshift({ ...created, videos: created.videos || [] });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadPlaylistsIndex.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadPlaylistsIndex.fulfilled, (state, action) => {
        state.loading = false;
        state.playlists = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(loadPlaylistsIndex.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load playlists';
      })
      .addCase(addEventToFavorites.fulfilled, (state, action) => {
        const { playlistId, videos } = action.payload || {};
        if (playlistId == null) return;
        const idx = state.playlists.findIndex((p) => p.id === playlistId);
        if (idx >= 0) state.playlists[idx].videos = videos ?? [];
      })
      .addCase(removeEventFromFavorites.fulfilled, (state, action) => {
        const { playlistId, videoId } = action.payload || {};
        if (playlistId == null || !videoId) return;
        const idx = state.playlists.findIndex((p) => p.id === playlistId);
        if (idx < 0) return;
        state.playlists[idx].videos = (state.playlists[idx].videos || [])
          .filter((v) => v.id !== videoId);
      })
      .addCase(removePlaylist.fulfilled, (state, action) => {
        const { playlistId } = action.payload || {};
        if (playlistId == null) return;
        state.playlists = state.playlists.filter((p) => p.id !== playlistId);
      })
      .addCase(renamePlaylist.fulfilled, (state, action) => {
        const { playlistId, title } = action.payload || {};
        if (playlistId == null || !title) return;
        const idx = state.playlists.findIndex((p) => p.id === playlistId);
        if (idx >= 0) state.playlists[idx].title = title;
      })
      .addCase(saveCurrentPlaylist.fulfilled, (state, action) => {
        const newPlaylist = action.payload;
        if (!newPlaylist?.id) return;
        // Insert above any non-Favorites entries but below the Favorites
        // pinned row, matching how the page renders them.
        const favIdx = state.playlists.findIndex((p) => p.title === FAVORITES_TITLE);
        const insertAt = favIdx >= 0 ? favIdx + 1 : 0;
        state.playlists.splice(insertAt, 0, newPlaylist);
      });
  },
});

export const { actions: playerFavoritesActions } = slice;
export const { reducer: playerFavoritesReducer } = slice;

// ---- selectors -----------------------------------------------------------

// All playlists, sorted so Favorites is first.
export const selectAllPlaylists = (state) => {
  const list = state.playerFavorites.playlists;
  const fav = list.find((p) => p.title === FAVORITES_TITLE);
  if (!fav) return list;
  return [fav, ...list.filter((p) => p !== fav)];
};

export const selectFavoritesPlaylist = (state) =>
  state.playerFavorites.playlists.find((p) => p.title === FAVORITES_TITLE) || null;

// Is this specific event marker present in the Favorites playlist? Used by
// the star toggle on event rows.
export const selectIsFavoriteEvent = (state, eventId) => {
  const fav = selectFavoritesPlaylist(state);
  if (!fav) return false;
  return (fav.videos || []).some((v) => String(eventIdOf(v)) === String(eventId));
};

// Resolves an event marker id to the playlist-item id needed for DELETE.
export const selectFavoriteVideoIdForEvent = (state, eventId) => {
  const fav = selectFavoritesPlaylist(state);
  if (!fav) return null;
  const video = (fav.videos || []).find((v) => String(eventIdOf(v)) === String(eventId));
  return video?.id ?? null;
};

export const selectPlaylistsLoading = (state) => state.playerFavorites.loading;
